/** Build marketplace event deep links from stored event and VL marketplace ids. */

export type MarketplaceDeepLink = {
  id: 'stubhub' | 'seatgeek' | 'ticketmaster' | 'vividseats' | 'tickpick' | 'gametime';
  label: string;
  href: string;
};

export function normalizeMarketplaceEventId(value: unknown): string | null {
  if (value == null) return null;
  const id = String(value).trim();
  if (!id || /^(null|undefined)$/i.test(id)) return null;
  return id;
}

export function normalizeEventApiUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function buildStubHubEventUrl(stubhubId: string): string {
  return `https://www.stubhub.com/event/${encodeURIComponent(stubhubId)}`;
}

export function buildSeatGeekEventUrl(seatgeekId: string): string {
  // VL only gives seatgeek_id — build the link ourselves (SeatGeek event page).
  return `https://seatgeek.com/e/events/${encodeURIComponent(seatgeekId)}`;
}

export function buildTicketmasterEventUrl(ticketmasterId: string): string {
  return `https://www.ticketmaster.com/event/${encodeURIComponent(ticketmasterId)}`;
}

/** Spaces become `+`; other characters stay percent-encoded (`:` → `%3A`). */
function encodeMarketplacePlusQuery(value: string): string {
  return encodeURIComponent(value.trim()).replace(/%20/g, '+');
}

/** TickPick search slugs are lowercase with hyphens between words. */
function encodeTickPickQuery(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Accept YYYY-MM-DD or an ISO timestamp and return the calendar date. */
export function normalizeMarketplaceSearchDate(value?: string | null): string | null {
  const match = String(value || '').trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

export function buildStubHubSearchUrl(eventName: string): string {
  return `https://www.stubhub.com/secure/Search?q=${encodeMarketplacePlusQuery(eventName)}`;
}

export function buildSeatGeekSearchUrl(eventName: string): string {
  return `https://seatgeek.com/search?f=1&search=${encodeMarketplacePlusQuery(eventName)}`;
}

export function buildTicketmasterSearchUrl(eventName: string, eventDate?: string | null): string {
  const query = encodeMarketplacePlusQuery(eventName);
  const date = normalizeMarketplaceSearchDate(eventDate);
  if (!date) return `https://www.ticketmaster.com/search?q=${query}`;
  return `https://www.ticketmaster.com/search?q=${query}&sort=date&startDate=${date}&endDate=${date}`;
}

export function buildVividSeatsSearchUrl(eventName: string, eventDate?: string | null): string {
  const query = encodeURIComponent(eventName.trim());
  const date = normalizeMarketplaceSearchDate(eventDate);
  if (!date) return `https://www.vividseats.com/search?searchTerm=${query}`;
  return `https://www.vividseats.com/search?startDate=${date}&endDate=${date}&searchTerm=${query}`;
}

export function buildTickPickSearchUrl(eventName: string): string {
  return `https://www.tickpick.com/search/?q=${encodeTickPickQuery(eventName)}`;
}

export function buildGameTimeSearchUrl(eventName: string): string {
  return `https://gametime.co/search?q=${encodeMarketplacePlusQuery(eventName)}`;
}

const MARKETPLACE_HOME_URLS = {
  stubhub: 'https://www.stubhub.com/',
  seatgeek: 'https://seatgeek.com/',
  ticketmaster: 'https://www.ticketmaster.com/',
  vividseats: 'https://www.vividseats.com/',
  tickpick: 'https://www.tickpick.com/',
  gametime: 'https://gametime.co/',
} as const;

export function getMarketplaceDeepLinks(input: {
  stubhubId?: string | null;
  seatgeekId?: string | null;
  ticketmasterId?: string | null;
  vividSeatsId?: string | null;
  eventApiUrl?: string | null;
  eventName?: string | null;
  /** Venue-local calendar date (`YYYY-MM-DD`) used by Vivid Seats and Ticketmaster. */
  eventDate?: string | null;
  venue?: string | null;
}): MarketplaceDeepLink[] {
  const links: MarketplaceDeepLink[] = [];
  const stubhubId = normalizeMarketplaceEventId(input.stubhubId);
  const seatgeekId = normalizeMarketplaceEventId(input.seatgeekId);
  const ticketmasterId = normalizeMarketplaceEventId(input.ticketmasterId);
  const vividSeatsId = normalizeMarketplaceEventId(input.vividSeatsId);
  const eventApiUrl = normalizeEventApiUrl(input.eventApiUrl);
  const eventName = input.eventName?.trim();
  const eventDate = normalizeMarketplaceSearchDate(input.eventDate);
  const tickPickQuery = eventName ? encodeTickPickQuery(eventName) : '';

  links.push({
    id: 'stubhub',
    label: 'StubHub',
    href: stubhubId
      ? buildStubHubEventUrl(stubhubId)
      : eventName
        ? buildStubHubSearchUrl(eventName)
        : MARKETPLACE_HOME_URLS.stubhub,
  });
  links.push({
    id: 'seatgeek',
    label: 'SeatGeek',
    href: seatgeekId
      ? buildSeatGeekEventUrl(seatgeekId)
      : eventName
        ? buildSeatGeekSearchUrl(eventName)
        : MARKETPLACE_HOME_URLS.seatgeek,
  });
  links.push({
    id: 'ticketmaster',
    label: 'Ticketmaster',
    href: eventName
      ? buildTicketmasterSearchUrl(eventName, eventDate)
      : eventApiUrl || (ticketmasterId
        ? buildTicketmasterEventUrl(ticketmasterId)
        : MARKETPLACE_HOME_URLS.ticketmaster),
  });
  links.push({
    id: 'vividseats',
    label: 'Vivid Seats',
    href: eventName
      ? buildVividSeatsSearchUrl(eventName, eventDate)
      : vividSeatsId
        ? `https://www.vividseats.com/event/${encodeURIComponent(vividSeatsId)}`
        : MARKETPLACE_HOME_URLS.vividseats,
  });
  links.push({
    id: 'tickpick',
    label: 'TickPick',
    href: tickPickQuery
      ? `https://www.tickpick.com/search/?q=${tickPickQuery}`
      : MARKETPLACE_HOME_URLS.tickpick,
  });
  links.push({
    id: 'gametime',
    label: 'GameTime',
    href: eventName
      ? buildGameTimeSearchUrl(eventName)
      : MARKETPLACE_HOME_URLS.gametime,
  });
  return links;
}
